// src/taskpane/taskpane.ts
// [Controller Layer] 完整的业务编排
/* global Office, window, localStorage, HTMLInputElement, HTMLTextAreaElement, AbortController, fetch, setTimeout, clearTimeout, document, console, Event, HTMLElement, URLSearchParams, navigator */
// Word and HTMLButtonElement are used indirectly through Office API and DOM operations
import {
  ModelProfile,
  ChatMessage,
  ContextData,
  DOM,
  getPromptModes,
  AppSettings,
  ProfileManager,
  BACKEND_URL,
  applyLanguageUI,
  I18N_DICT,
  ICONS,
} from "./modules/store";
import {
  Logger,
  getEl,
  updateFeedback,
  toggleLoading,
  setVisualMode,
  switchTab,
  updateBreadcrumb,
  setDisabled,
  initSettingsPanelBehavior,
  initInputHandlers,
  renderProfileListUI,
  openProfileFormUI,
  closeProfileFormUI,
  getProfileFormData,
  bindProfileNameSync,
  syncUI,
} from "./modules/view";
import { AIStreamEngine } from "./modules/engine";
import {
  getWordContext,
  insertToDoc,
  insertMarkdownOutline,
  initDocumentIdentity,
  loadTaskConfig,
  saveTaskConfig,
  cleanDocumentMetadata,
  getSmartSelection,
} from "./modules/word";

// --- State ---
let chatHistory: ChatMessage[] = [];
let profiles: ModelProfile[] = [];
let currentProfileId: string = ""; // 正在使用的配置 (Green)
let editingProfileId: string = ""; // 正在编辑的配置 (Blue)
let currentDocId: string = "";
let globalContext: ContextData = { text: "", breadcrumbHtml: "准备就绪", isHeading: false };
let isRunning = false;
let lastGenState = { mode: "", sourceText: "", isHeading: false };
let currentLang: "en" | "zh" = "zh"; // 当前语言环境
let currentAbortController: AbortController | null = null; // 引擎控制句柄

// UI 状态追踪对象
let uiState = {
  isRunning: false,
  isHeading: false,
};

/**
 * 请求 UI 同步
 */
function requestUISync() {
  syncUI(uiState);
}

// --- Office 初始化入口 ---
Office.onReady(async (info) => {
  // [场景 A] 浏览器/非 Word 环境调试 (解决 localhost 白屏问题)
  if (info.host !== Office.HostType.Word) {
    console.warn("⚠️ 当前运行于浏览器或非 Word 环境，已启用调试模式");
    document.body.classList.remove("cloak"); // 立即揭幕

    // 模拟基础环境，方便调试 CSS 和布局
    currentLang = "zh";
    initUI();
    try {
      applyLanguageUI("zh");
    } catch {
      // 忽略浏览器环境下的语言应用错误
    }
    return;
  }

  // [场景 B] Word 生产/开发环境
  try {
    // --- 阶段 1: 极速启动 (本地逻辑) ---

    // 1. 语言与基础 UI
    // 优化 currentLang 的初始化算法，增加空值容错
    // 优先获取 Office 语言，其次系统语言，最后兜底为 zh
    const rawLang = (
      Office.context.displayLanguage ||
      window.navigator.language ||
      "zh-CN"
    ).toLowerCase();
    // 注入环境诊断日志
    Logger.info(
      `[Env Diagnostic] Office.context.displayLanguage: ${Office.context.displayLanguage}`
    );
    Logger.info(`[Env Diagnostic] window.navigator.language: ${window.navigator.language}`);
    // 只要不是以 en 开头，一律强制判定为 zh（确保中文优先）
    currentLang = rawLang.startsWith("en") ? "en" : "zh";
    Logger.info(`[Env Diagnostic] 判定后的初始 currentLang: ${currentLang}`);
    applyLanguageUI(currentLang);
    initSettingsPanelBehavior(currentLang);
    initUI();

    // [Phase 1.6] 动态注入法律页面链接的语言参数
    syncLegalLinks(currentLang);

    // 2. 揭开蒙皮 (Cloak)
    // [策略]：此时 UI 骨架已就绪，立即移除蒙皮，让用户看到界面。
    // 同时开启 Loading 遮罩，告诉用户“正在连接数据”。
    // 这样用户体验是：白屏 -> 界面+转圈 -> 界面可用，而不是一直白屏。
    document.body.classList.remove("cloak");
    toggleLoading(true); // 阻断交互，显示“加载中...”

    // 3. Word 上下文初始化
    setupSelectionHandler();
    currentDocId = await initDocumentIdentity(); // 如果文档受保护，这里可能报错
    loadTaskConfig(currentDocId);
    profiles = ProfileManager.load();

    // --- 阶段 2: 远程同步 (高风险逻辑) ---

    // 4. 后端同步 (允许失败)
    // 使用 try-catch 包裹 await，防止网络错误中断整个初始化
    let syncResult: any = { reviewMode: false };
    try {
      syncResult = await AppSettings.sync(currentLang);
      // 记录 syncResult.reviewMode 的布尔值，以排查后端误判
      Logger.info(
        `[Env Diagnostic] AppSettings.sync() 返回的 reviewMode: ${syncResult.reviewMode}`
      );
    } catch {
      console.warn("[Init] 后端同步失败，降级使用本地配置");
      // 可以在这里给个轻微的 toast 提示，或者静默失败
    }

    // 5. Review Mode 逻辑 (仅在同步成功且开启时触发)
    if (syncResult.reviewMode && currentLang !== "en") {
      Logger.info("[Init] Review Mode 触发，切换英文环境");
      currentLang = "en";

      // 重新应用 UI 和设置
      applyLanguageUI(currentLang);
      initSettingsPanelBehavior(currentLang);

      // 清理本地 Prompt 缓存 (防止旧中文 Prompt 残留)
      getPromptModes("en").forEach((m) => localStorage.removeItem(`bid_prompt_${m.value}`));

      // 尝试同步英文 Prompt
      try {
        const enSync: any = await AppSettings.sync("en");
        if (enSync.prompts && typeof (window as any).DEFAULT_PROMPTS !== "undefined") {
          Object.assign((window as any).DEFAULT_PROMPTS, enSync.prompts);
        }
      } catch {
        console.warn("[Init] 英文 Prompt 同步失败");
      }
    }

    // 6. 模型注入
    if (profiles.length === 0 && syncResult.reviewMode) {
      const demoName = "DeepBid Demo";
      profiles.push({
        id: "reviewer_demo",
        name: demoName,
        url: BACKEND_URL,
        model: "deepseek-chat",
        apiKey: "DEEPBID_REVIEWER_ONLY",
      });
      ProfileManager.save(profiles);
    }

    // --- 阶段 3: 最终渲染 ---

    initProfileSelection();
    renderQuickSelect();

    // 刷新设置面板 (如果处于打开状态)
    if (getEl(DOM.SETTINGS_OVERLAY)?.style.display === "flex") {
      renderProfileListUI(
        profiles,
        currentProfileId,
        editingProfileId,
        (p) => handleProfileClick(p),
        currentLang
      );
    }

    // 视觉模式与 Prompt 加载
    setVisualMode("content", currentLang);
    const readyText = currentLang === "en" ? "Ready" : "准备就绪";
    updateFeedback(DOM.STATUS_BAR, readyText, false);

    initPromptUI();
    loadSelectedPromptToEditor();
  } catch {
    // [全局错误捕获]
    // 如果出错，状态栏变红，提示用户
    const errMsg = currentLang === "en" ? "Init Failed" : "初始化失败";
    updateFeedback(DOM.STATUS_BAR, errMsg, true);
  } finally {
    // [兜底保障]
    // 无论上面成功还是报错，最后必须：
    // 1. 移除 Loading 遮罩 (允许用户点击)
    // 2. 确保蒙皮已移除 (防止 try 块第一行就挂了导致还没移除)
    // 3. 同步 UI 状态，确保按钮状态正确
    toggleLoading(false);
    document.body.classList.remove("cloak");
    requestUISync();
  }
});

// --- 1. Core Task Logic ---
async function runTask(mode: "generate" | "polish" | "check" | "revise" | "outline") {
  // 1. 基础环境校验
  const profile = profiles.find((p) => p.id === currentProfileId);
  if (!profile?.apiKey) {
    openApiSettings("请配置 API Key", true);
    return;
  }
  if (isRunning) return;

  // 2. 获取输入数据
  const instruction = getEl<HTMLInputElement>(DOM.INSTRUCTION)?.value || "";
  // [关键] 提前捕获当前预览区内容，既作为发送给AI的数据，也作为出错回滚的备份
  const currentContent = getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT)?.value || "";

  // 3. 智能判断目标格式
  let targetFormat = "content";
  if (mode === "outline") {
    targetFormat = "outline";
  } else if (mode === "revise") {
    if (
      lastGenState.mode === "outline" ||
      getEl(DOM.FINAL_OUTPUT)?.classList.contains("mode-outline")
    ) {
      targetFormat = "outline";
    }
  }

  // 4. [逻辑] 智能选区扩展 (仅针对 润色/检查)
  if (mode === "polish" || mode === "check") {
    if (!globalContext.text) {
      try {
        toggleLoading(true);
        updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-fetching"]);
        const smartData = await getSmartSelection();
        if (smartData.text && smartData.text.trim().length > 0) {
          globalContext.text = smartData.text;
        }
      } catch {
        console.warn("[Task] Smart selection failed");
      } finally {
        toggleLoading(false);
      }
    }
  }

  // 5. 前置条件校验
  if (mode === "revise" && !currentContent) {
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-waiting"], true);
    return;
  }
  if (mode !== "revise" && !globalContext.text && mode !== "generate" && mode !== "outline") {
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-select-content"], true);
    return;
  }
  if ((mode === "generate" || mode === "outline") && !globalContext.isHeading) {
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-select-heading"], true);
    return;
  }
  if ((mode === "polish" || mode === "check") && globalContext.isHeading) {
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-select-content"], true);
    return;
  }

  // 6. [逻辑] 历史记录管理 (滑动窗口)
  if (mode !== "revise") {
    // 非修订模式，彻底清空历史，保证上下文纯净
    chatHistory = [];
  } else {
    // 修订模式，仅保留最近 3 轮 (6条消息) 防止 Token 爆炸
    const MAX_HISTORY_TURNS = 3;
    const MAX_HISTORY_MSGS = MAX_HISTORY_TURNS * 2;
    if (chatHistory.length > MAX_HISTORY_MSGS) {
      chatHistory = chatHistory.slice(-MAX_HISTORY_MSGS);
      Logger.info(`[History] 已裁切历史记录至最近 ${MAX_HISTORY_TURNS} 轮`);
    }
  }

  try {
    isRunning = true;
    uiState.isRunning = true;
    requestUISync();
    toggleLoading(true);

    // [UX] 根据目标格式设定视觉模式 (防止修订大纲时跳回正文样式)
    setVisualMode(targetFormat === "outline" ? "outline" : "content", currentLang);
    switchTab("brain", currentLang);

    if (mode !== "revise") {
      lastGenState = {
        mode,
        sourceText: globalContext.text,
        isHeading: globalContext.isHeading,
      };
    }

    getEl<HTMLTextAreaElement>(DOM.BRAIN_OUTPUT)!.value = "";
    getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT)!.value = "";

    // 初始化 AbortController 用于前端主动终止
    currentAbortController = new AbortController();

    // 启动流式引擎
    const engine = new AIStreamEngine({
      url: `${BACKEND_URL}/api/chat`,
      onThought: (t) => appendText(DOM.BRAIN_OUTPUT, t),
      onContent: (c) => appendText(DOM.FINAL_OUTPUT, c),
      onTransition: (s) => switchTab(s === "thinking" ? "brain" : "preview", currentLang),
    });

    await engine.start(
      {
        apiKey: profile.apiKey,
        model: profile.model,
        providerUrl: profile.url,
        mode,
        lang: currentLang, // 将当前语言环境发往后端
        targetFormat: targetFormat, // [关键] 传递格式意图给后端
        temperature: AppSettings.getTemp(mode),
        customPrompt: AppSettings.hasOverride(mode) ? AppSettings.getPrompt(mode) : null,
        contextData: globalContext,
        instruction,
        currentDraft: currentContent, // 传递旧文本作为 "唯一真理"
        globalRules: getEl<HTMLTextAreaElement>(DOM.GLOBAL_RULES)?.value,
        projectInfo: getEl<HTMLTextAreaElement>(DOM.RED_BOOK)?.value,
        chatHistory,
      },
      currentAbortController.signal
    );

    const result = getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT)?.value || "";

    // 9. [逻辑] 历史追加
    if (mode === "revise") {
      chatHistory.push({ role: "user", content: instruction });
      chatHistory.push({ role: "assistant", content: result });
      // 清空输入框
      getEl<HTMLInputElement>(DOM.INSTRUCTION)!.value = "";
    }
  } catch (e) {
    // [修复] 错误处理国际化
    const isAbort = e.name === "AbortError";
    const msg = isAbort
      ? I18N_DICT[currentLang]["err-abort"]
      : `${I18N_DICT[currentLang]["status-insert-fail"]}${e.message}`;
    updateFeedback(DOM.STATUS_BAR, msg, true);

    // 记录用户中止日志
    if (isAbort) {
      Logger.info("[User Cancelled] 用户主动中止任务");
    }

    if (mode === "revise" && currentContent) {
      const outputEl = getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT);
      if (outputEl && outputEl.value.length < 10) {
        outputEl.value = currentContent;
        // [修复] 回滚提示国际化
        updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["err-rollback"], true);
      }
    }
  } finally {
    isRunning = false;
    uiState.isRunning = false;
    // 状态复位
    currentAbortController = null;
    requestUISync();
    toggleLoading(false);
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-ready"]);

    // 11. [UX] 状态最终一致性恢复
    // 再次强制刷新按钮文案，解决 "任务完成后按钮文字跳变" 的问题
    setVisualMode(targetFormat === "outline" ? "outline" : "content", currentLang);

    // 手动触发 input 事件，解决 "输入框有内容但按钮仍置灰" 的问题
    getEl(DOM.INSTRUCTION)?.dispatchEvent(new Event("input"));
  }
}

async function runRefineTask() {
  const profile = profiles.find((p) => p.id === currentProfileId);
  if (!profile?.apiKey) {
    openApiSettings("请配置 API Key", true);
    return;
  }

  const rb = getEl<HTMLTextAreaElement>(DOM.RED_BOOK);
  if (!rb?.value.trim()) {
    updateFeedback(DOM.TASK_FEEDBACK, "请先粘贴文本", true);
    return;
  }

  if (isRunning) return;
  isRunning = true;
  uiState.isRunning = true;
  requestUISync();
  toggleLoading(true);
  try {
    // 初始化 AbortController 用于前端主动终止
    currentAbortController = new AbortController();

    const engine = new AIStreamEngine({
      url: `${BACKEND_URL}/api/chat`,
      onThought: () => {},
      onTransition: () => {},
      onContent: (t) => {
        rb.value += t;
      },
    });
    rb.value = "";
    updateFeedback(DOM.TASK_FEEDBACK, I18N_DICT[currentLang]["status-refining"]);
    await engine.start(
      {
        apiKey: profile.apiKey,
        model: profile.model,
        mode: "refine",
        temperature: AppSettings.getTemp("refine"),
        contextData: { rawText: rb.value },
      },
      currentAbortController.signal
    );
    updateFeedback(DOM.TASK_FEEDBACK, I18N_DICT[currentLang]["status-refine-done"], false);
  } catch (e) {
    // 特殊处理 AbortError，不弹出报错弹窗
    const isAbort = e.name === "AbortError";
    if (isAbort) {
      Logger.info("[User Cancelled] 用户主动中止提炼任务");
    } else {
      updateFeedback(
        DOM.TASK_FEEDBACK,
        I18N_DICT[currentLang]["status-refine-fail"] + e.message,
        true
      );
    }
  } finally {
    isRunning = false;
    uiState.isRunning = false;
    // 状态复位
    currentAbortController = null;
    requestUISync();
    toggleLoading(false);
  }
}

async function handleInsert() {
  const text = getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT)?.value;
  if (!text) return;

  try {
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-writing"]);

    if (lastGenState.mode === "outline") {
      await insertMarkdownOutline(text);
    } else {
      await insertToDoc();
    }

    // --- 成功反馈 ---
    updateFeedback(DOM.STATUS_BAR, I18N_DICT[currentLang]["status-insert-done"], false);
    Logger.info("[Word] 插入动作成功执行");
  } catch (error) {
    // --- 失败反馈 ---
    console.error("Insert failed:", error);
    const failMsg =
      (I18N_DICT[currentLang]["status-insert-fail"] || "Insert Fail: ") + error.message;
    updateFeedback(DOM.STATUS_BAR, failMsg, true);
  }
}

// --- 2. UI & Event Bindings ---

function initUI() {
  bind(DOM.BTN_GENERATE, () => runTask("generate"));
  bind(DOM.BTN_OUTLINE, () => runTask("outline"));
  bind(DOM.BTN_POLISH, () => runTask("polish"));
  bind(DOM.BTN_CHECK, () => runTask("check"));
  bind(DOM.BTN_SUBMIT_REVISE, () => {
    // 灵动停止逻辑：如果任务正在运行，则中止；否则正常启动
    if (uiState.isRunning && currentAbortController) {
      console.log("[User Cancelled] 用户主动中止任务");
      currentAbortController.abort();
      // 即时反馈：将按钮暂时设为 disabled 防止连击
      const btn = getEl(DOM.BTN_SUBMIT_REVISE);
      if (btn) {
        btn.setAttribute("disabled", "true");
        setTimeout(() => btn.removeAttribute("disabled"), 300);
      }
    } else {
      runTask("revise");
    }
  });
  initInputHandlers(() => {
    // 输入处理器的发送逻辑也需要支持灵动停止
    if (uiState.isRunning && currentAbortController) {
      console.log("[User Cancelled] 用户主动中止任务 (快捷键)");
      currentAbortController.abort();
    } else {
      runTask("revise");
    }
  });
  bind(DOM.BTN_INSERT, handleInsert);
  bind(DOM.BTN_REFINE_REDBOOK, runRefineTask); // 假设之前定义的 runRefineTask 存在

  bind(DOM.TAB_PREVIEW, () => switchTab("preview", currentLang));
  bind(DOM.TAB_BRAIN, () => switchTab("brain", currentLang));

  bind(DOM.BTN_OPEN_SETTINGS, () => {
    getEl(DOM.SETTINGS_OVERLAY)!.style.display = "flex";
    editingProfileId = "";
    refreshProfileList();
  });
  bind(DOM.BTN_CLOSE_SETTINGS, () => {
    handleCancelEdit();
    getEl(DOM.SETTINGS_OVERLAY)!.style.display = "none";
    closeProfileFormUI();
  });
  const overlay = getEl(DOM.SETTINGS_OVERLAY);
  if (overlay) {
    overlay.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      // 查找是否点击了模型配置的 Header (向上查找 closest)
      const header = target.closest(".section-header");

      // 如果点击的是"模型配置"的标题栏，且当前有正在编辑的内容
      if (header && header.getAttribute("data-target") === DOM.AREA_API) {
        // 只要点击了折叠/展开，就视为放弃当前的未保存编辑
        handleCancelEdit();
      }
    });
  }

  bind(DOM.BTN_SAVE_TASK, () => saveTaskConfig(currentDocId));
  bind(DOM.BTN_CANCEL_TASK, () => loadTaskConfig(currentDocId));
  bind(DOM.BTN_CLEAN_DOC, () => {
    cleanDocumentMetadata(currentDocId, currentLang, (btn) => {
      btn.classList.remove("confirm-state");
      btn.innerHTML = ICONS.BROOM;
      btn.style.color = "";
    });
  });

  // Profile CRUD
  bind(DOM.BTN_ADD_PROFILE, handleAddProfile);
  bind(DOM.BTN_CANCEL_EDIT, handleCancelEdit);
  bind(DOM.BTN_TEST, testApiConnection);
  bind("btn-del-profile", () => {
    const id = getEl<HTMLInputElement>(DOM.HIDDEN_ID)?.value;
    if (id) deleteProfile(id);
  });

  // Prompt Settings
  bind(DOM.BTN_SAVE_PROMPT, saveCurrentPrompt);
  bind(DOM.BTN_RESET_PROMPT, resetCurrentPrompt);

  bind(DOM.BTN_COPY_LOGS, () => {
    const logs = Logger.export();
    navigator.clipboard
      .writeText(logs)
      .then(() => {
        updateFeedback(DOM.API_FEEDBACK, I18N_DICT[currentLang]["status-logs-copied"], false);
      })
      .catch((err) => {
        console.error("Failed to copy logs:", err);
        // 降级方案：使用旧的 execCommand 方法
        const el = document.createElement("textarea");
        el.value = logs;
        document.body.appendChild(el);
        el.select();
        try {
          document.execCommand("copy");
          updateFeedback(DOM.API_FEEDBACK, I18N_DICT[currentLang]["status-logs-copied"], false);
        } catch (fallbackErr) {
          console.error("Fallback copy also failed:", fallbackErr);
          updateFeedback(DOM.API_FEEDBACK, "复制失败，请检查控制台", true);
        } finally {
          document.body.removeChild(el);
        }
      });
  });
}

function handleProfileClick(p: ModelProfile) {
  editingProfileId = p.id; // 仅标记为“正在编辑”
  refreshProfileList(); // 刷新列表显示蓝色高亮

  openProfileFormUI(p, currentLang);

  // 绑定改名同步
  bindProfileNameSync((newName) => {
    p.name = newName;
    refreshProfileList();
  });
}

function handleAddProfile() {
  const dict = I18N_DICT[currentLang];
  const tempProfile: ModelProfile = {
    id: "new_temp",
    name: dict["profile-default-name"],
    url: "https://api.deepseek.com/v1/chat/completions",
    model: "deepseek-chat",
    apiKey: "",
  };

  const existTemp = profiles.find((p) => p.id === "new_temp");
  if (!existTemp) {
    profiles.push(tempProfile);
  }

  // 选中它进行编辑，但不设为 current (不自动切换绿色)
  handleProfileClick(existTemp || tempProfile);
}

// [修复问题 1] 处理取消：如果取消的是还没保存的临时对象，要删掉
function handleCancelEdit() {
  const editingId = getEl<HTMLInputElement>(DOM.HIDDEN_ID)?.value;
  if (editingId === "new_temp") {
    profiles = profiles.filter((p) => p.id !== "new_temp");
  }
  editingProfileId = ""; // 退出编辑状态
  closeProfileFormUI();
  refreshProfileList();
}

function setupSelectionHandler() {
  let timer: number;
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () => {
    // [修复] 卫语句：防止任务运行中状态被重置
    if (isRunning) return;

    clearTimeout(timer);
    timer = window.setTimeout(async () => {
      try {
        const data = await getWordContext();
        globalContext = data;
        uiState.isHeading = data.isHeading;
        requestUISync();
        updateBreadcrumb(data.breadcrumbHtml);
        updateContextButtons();
      } catch {
        // 忽略获取 Word 上下文时的错误
      }
    }, 100);
  });
}

// --- 3. Profile Management Logic (MVC Refactored) ---

function initProfileSelection() {
  if (profiles.length === 0) {
    currentProfileId = "";
    return;
  }
  const storedId = localStorage.getItem("bid_currentProfileId");
  currentProfileId =
    storedId && profiles.some((p) => p.id === storedId) ? storedId : profiles[0].id;
  renderQuickSelect();
}

function renderQuickSelect() {
  const trigger = getEl(DOM.DROPDOWN_TRIGGER);
  const menu = getEl(DOM.DROPDOWN_MENU);
  const txt = getEl(DOM.CURRENT_MODEL_TEXT);
  if (!trigger || !menu || !txt) return;

  const dict = I18N_DICT[currentLang];
  const curr = profiles.find((p) => p.id === currentProfileId);
  txt.innerText = curr ? curr.name : dict["status-no-model"];

  menu.innerHTML = "";
  profiles.forEach((p) => {
    const item = document.createElement("div");
    item.className = `dropdown-item ${p.id === currentProfileId ? "selected" : ""}`;
    item.innerText = p.name;
    item.onclick = (e) => {
      e.stopPropagation();
      currentProfileId = p.id;
      localStorage.setItem("bid_currentProfileId", currentProfileId);
      renderQuickSelect();
      menu.classList.remove("show");
      trigger.classList.remove("active");
    };
    menu.appendChild(item);
  });
  trigger.onclick = (e) => {
    e.stopPropagation();
    if (!isRunning) {
      menu.classList.toggle("show");
      trigger.classList.toggle("active");
    }
  };
  window.onclick = () => {
    menu.classList.remove("show");
    trigger.classList.remove("active");
  };
}

// [重构] 刷新列表：连接 Controller 数据与 View 呈现
function refreshProfileList() {
  renderProfileListUI(
    profiles,
    currentProfileId,
    editingProfileId,
    (profile) => {
      handleProfileClick(profile);
    },
    currentLang
  );
}

// [重构] 删除逻辑
function deleteProfile(id: string) {
  profiles = profiles.filter((p) => p.id !== id);
  if (currentProfileId === id) currentProfileId = profiles[0]?.id || "";
  ProfileManager.save(profiles);
  renderQuickSelect();
  closeProfileFormUI();
  refreshProfileList();
}

// [重构] API 测试：获取 View 数据 -> 测试 -> 调用保存
async function testApiConnection() {
  const formData = getProfileFormData();
  const btn = getEl(DOM.BTN_TEST);
  const dict = I18N_DICT[currentLang];

  if (!formData.apiKey) {
    updateFeedback(DOM.API_FEEDBACK, dict["status-missing-key"], true);
    return;
  }

  setDisabled(btn, true, { loadingText: dict["status-loading"] });
  updateFeedback(DOM.API_FEEDBACK, "");

  let isSuccess = false;
  let errorMsg = "";

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const res = await fetch(formData.url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${formData.apiKey}` },
      body: JSON.stringify({
        model: formData.model,
        messages: [{ role: "user", content: "Hi" }],
        max_tokens: 5,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      let detail = res.statusText;
      try {
        const json = await res.json();
        if (json.error) detail = json.error.message;
      } catch {
        // 忽略 JSON 解析错误，使用默认错误信息
      }
      throw new Error(`HTTP ${res.status}: ${detail}`);
    }

    isSuccess = true;
  } catch (e) {
    console.error("API Test Error:", e);
    errorMsg = e.message;
    if (e.name === "AbortError") errorMsg = "请求超时";
    if (e.message.includes("Failed to fetch")) errorMsg = "网络失败 (可能被 CORS/CSP 拦截)";
  } finally {
    // [关键] 必须在这里恢复，且确保在 UI 刷新前恢复
    setDisabled(btn, false);
  }

  if (isSuccess) {
    if (btn) {
      btn.classList.add("btn-status-success");
      btn.innerHTML = `<span>已保存 ✓</span>`; // 这里的文字会覆盖原来的“测试并保存”
    }
    updateFeedback(DOM.API_FEEDBACK, dict["status-verified"], false);

    // 保存逻辑
    if (formData.id === "new_temp") {
      profiles = profiles.filter((p) => p.id !== "new_temp");
      formData.id = Date.now().toString();

      // 新建的模型，默认设为当前使用 (Green)
      currentProfileId = formData.id;
    } else {
      // 如果编辑的是当前正在用的模型，更新后它依然是当前模型
      if (editingProfileId === currentProfileId) {
        // do nothing, just update data
      }
      // 如果编辑的是其他模型，点保存后，是否要自动切过去？
      // 通常逻辑：保存即选中。
      currentProfileId = formData.id;
    }

    // 无论如何，保存完后，编辑ID更新为正式ID
    editingProfileId = formData.id;

    const idx = profiles.findIndex((p) => p.id === formData.id);
    if (idx !== -1) profiles[idx] = formData;
    else profiles.push(formData);

    ProfileManager.save(profiles);

    renderQuickSelect();
    setTimeout(() => {
      closeProfileFormUI();
      refreshProfileList();
    }, 800);
  } else {
    updateFeedback(DOM.API_FEEDBACK, `测试失败: ${errorMsg}`, true);
  }
}

// --- 4. Prompt Logic ---

function initPromptUI() {
  const container = getEl(DOM.LIST_PROMPT_CONTAINER);
  if (!container) return;
  container.innerHTML = "";
  getPromptModes(currentLang).forEach((m) => {
    const div = document.createElement("div");
    div.className = "profile-item";
    div.innerText = m.label;

    // 检查当前选中的 Prompt (UI 状态回显)
    const currentMode = getEl<HTMLInputElement>(DOM.PROMPT_SELECTOR)?.value || "generate";
    if (m.value === currentMode) div.classList.add("active");

    div.onclick = () => {
      getEl<HTMLInputElement>(DOM.PROMPT_SELECTOR)!.value = m.value;
      loadSelectedPromptToEditor();

      // 更新 UI 选中态
      Array.from(container.children).forEach((c) => c.classList.remove("active"));
      div.classList.add("active");
    };
    container.appendChild(div);
  });
}

function loadSelectedPromptToEditor() {
  const mode = getEl<HTMLInputElement>(DOM.PROMPT_SELECTOR)?.value || "generate";
  // 逻辑检查：AppSettings.getPrompt 会优先读 LocalStorage，其次读 Remote。逻辑正确。
  const promptText = AppSettings.getPrompt(mode);
  getEl<HTMLTextAreaElement>(DOM.PROMPT_EDITOR)!.value = promptText;
}

function saveCurrentPrompt() {
  const mode = getEl<HTMLInputElement>(DOM.PROMPT_SELECTOR)?.value;
  const val = getEl<HTMLTextAreaElement>(DOM.PROMPT_EDITOR)?.value;
  if (mode && val) {
    localStorage.setItem(`bid_prompt_${mode}`, val);
    // [修复] 传入 false，触发绿色 SVG 样式
    updateFeedback(DOM.PROMPT_FEEDBACK, "已保存", false);
  }
}

function resetCurrentPrompt() {
  const mode = getEl<HTMLInputElement>(DOM.PROMPT_SELECTOR)?.value;
  if (mode) {
    AppSettings.resetPrompt(mode);
    loadSelectedPromptToEditor();
    // [修复] 传入 false，触发绿色 SVG 样式
    updateFeedback(DOM.PROMPT_FEEDBACK, "已重置", false);
  }
}

// Helpers
function openApiSettings(msg: string, err: boolean) {
  getEl(DOM.BTN_OPEN_SETTINGS)?.click();
  // 打开设置时，清空编辑状态
  editingProfileId = "";
  refreshProfileList();

  // 如果需要强制弹新表单，解开下面这行。但通常只显示列表让用户选更好。
  // openProfileFormUI(null);

  updateFeedback(DOM.API_FEEDBACK, msg, err);
}

function updateContextButtons() {
  // 此函数现在已过时，UI状态由syncUI统一管理
  // 保留函数定义以避免破坏现有调用，但不再执行任何操作
}
function bind(id: string, fn: () => void) {
  getEl(id)?.addEventListener("click", fn);
}
function appendText(id: string, txt: string) {
  const el = getEl<HTMLTextAreaElement>(id);
  if (el) {
    el.value += txt;
    el.scrollTop = el.scrollHeight;
  }
}

/**
 * [Phase 1.6] 动态同步法律页面链接的语言参数
 * 在隐私协议和客户支持链接中注入当前语言参数
 */
function syncLegalLinks(lang: "en" | "zh") {
  try {
    // 获取所有法律相关链接
    const privacyLink = document.querySelector('a[href="privacy.html"]');
    const supportLink = document.querySelector('a[href="support.html"]');

    // 更新隐私协议链接
    if (privacyLink) {
      const baseUrl = "privacy.html";
      const newUrl = updateUrlWithLang(baseUrl, lang);
      privacyLink.setAttribute("href", newUrl);
    }

    // 更新客户支持链接
    if (supportLink) {
      const baseUrl = "support.html";
      const newUrl = updateUrlWithLang(baseUrl, lang);
      supportLink.setAttribute("href", newUrl);
    }

    Logger.info(`[Legal Links] 已同步法律页面链接语言参数: lang=${lang}`);
  } catch (error) {
    console.warn("[Legal Links] 同步法律页面链接失败:", error);
  }
}

/**
 * 更新URL中的语言参数，确保参数唯一性
 */
function updateUrlWithLang(baseUrl: string, lang: "en" | "zh"): string {
  // 移除现有的lang参数
  const urlWithoutParams = baseUrl.split("?")[0];
  const searchParams = new URLSearchParams(baseUrl.split("?")[1] || "");

  // 设置新的lang参数
  searchParams.set("lang", lang);

  // 构建新的URL
  const newSearch = searchParams.toString();
  return newSearch ? `${urlWithoutParams}?${newSearch}` : urlWithoutParams;
}
